import React, { useState, useMemo } from 'react';

import classnames from 'classnames/bind';

import { AdminVendor } from '@SERVICES';
import useGlobalPermissions from '@HOOKS/useGlobalPermissions';

import BasicButton from '@COMPONENTS/COMMON/buttons/BasicButton';
import { Edit3, Trash2, Search, X, ChevronLeft, ChevronRight, ChevronUp, ChevronDown, Upload } from 'lucide-react';
import VendorUsageIndicator from './VendorUsageIndicator.component';

// Import Lucide icons

import styles from './VendorsTable.module.scss';

const cx: CX = classnames.bind(styles);

const PAGE_SIZE = 10; // Items per page

type SortField = 'status' | 'contractStatus' | 'visibility' | null;
type SortDirection = 'asc' | 'desc';

interface SortConfig {
    field: SortField;
    direction: SortDirection;
}

interface VendorsTableProps {
    vendors: AdminVendor[];
    isLoading: boolean;
    onEdit: (vendor: AdminVendor) => void;
    onCreate: () => void;
    onImport: () => void;
    onDelete: (vendor: AdminVendor) => void;
    searchValue: string;
    onSearchChange: (value: string) => void;
    searchResultsCount: number;
    totalVendorsCount: number;
}

function VendorsTable(props: VendorsTableProps) {
    const {
        vendors,
        isLoading,
        onEdit,
        onCreate,
        onImport,
        onDelete,
        searchValue,
        onSearchChange,
        searchResultsCount,
        totalVendorsCount,
    } = props;

    const [currentPage, setCurrentPage] = useState(1);
    const [sortConfig, setSortConfig] = useState<SortConfig>({ field: null, direction: 'asc' });

    const {
        canManageVendors,
    } = useGlobalPermissions();

    // Reset to page 1 when search changes
    React.useEffect(() => {
        setCurrentPage(1);
    }, [searchValue]);

    // Sorting logic
    const sortedVendors = useMemo(() => {
        if (!sortConfig.field) return vendors;

        return [...vendors].sort((a, b) => {
            let aValue: string | boolean;
            let bValue: string | boolean;

            switch (sortConfig.field) {
                case 'status':
                    aValue = a.status || 'active';
                    bValue = b.status || 'active';
                    break;
                case 'contractStatus':
                    aValue = a.contractStatus || 'active';
                    bValue = b.contractStatus || 'active';
                    break;
                case 'visibility':
                    aValue = a.isVisible;
                    bValue = b.isVisible;
                    break;
                default:
                    return 0;
            }

            // Handle boolean values for visibility
            if (typeof aValue === 'boolean' && typeof bValue === 'boolean') {
                if (sortConfig.direction === 'asc') {
                    if (aValue === bValue) return 0;
                    return aValue ? 1 : -1;
                }
                if (aValue === bValue) return 0;
                return aValue ? -1 : 1;
            }

            // Handle string values for status fields
            if (typeof aValue === 'string' && typeof bValue === 'string') {
                const comparison = aValue.toLowerCase().localeCompare(bValue.toLowerCase());
                return sortConfig.direction === 'asc' ? comparison : -comparison;
            }

            return 0;
        });
    }, [vendors, sortConfig]);

    // Pagination calculations (use sorted vendors)
    const totalPages = Math.ceil(sortedVendors.length / PAGE_SIZE);
    const startIndex = (currentPage - 1) * PAGE_SIZE;
    const endIndex = startIndex + PAGE_SIZE;
    const paginatedVendors = sortedVendors.slice(startIndex, endIndex);
    const hasVendors = sortedVendors.length > 0;

    // Reset to page 1 when sorting changes
    React.useEffect(() => {
        setCurrentPage(1);
    }, [sortConfig]);

    // Pagination controls
    const canGoPrevious = currentPage > 1;
    const canGoNext = currentPage < totalPages;

    const handlePreviousPage = () => {
        if (canGoPrevious) {
            setCurrentPage(currentPage - 1);
        }
    };

    const handleNextPage = () => {
        if (canGoNext) {
            setCurrentPage(currentPage + 1);
        }
    };

    const handlePageClick = (page: number) => {
        setCurrentPage(page);
    };

    // Sorting handlers
    const handleSort = (field: SortField) => {
        if (!field) return;

        setSortConfig(prev => ({
            field,
            direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    const getSortIcon = (field: SortField) => {
        if (sortConfig.field !== field) {
            return <ChevronUp className={cx('sort-icon', 'neutral')} size={14} />;
        }

        if (sortConfig.direction === 'asc') {
            return <ChevronUp className={cx('sort-icon', 'active')} size={14} />;
        }
        return <ChevronDown className={cx('sort-icon', 'active')} size={14} />;
    };

    // Generate page numbers for pagination
    const getPageNumbers = () => {
        const pages = [];
        const maxVisiblePages = 5;

        if (totalPages <= maxVisiblePages) {
            // Show all pages if total is small
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
            }
        } else {
            // Show smart pagination with ellipsis
            let startPage = Math.max(1, currentPage - 2);
            let endPage = Math.min(totalPages, currentPage + 2);

            // Adjust if we're near the beginning or end
            if (currentPage <= 3) {
                endPage = Math.min(5, totalPages);
            } else if (currentPage >= totalPages - 2) {
                startPage = Math.max(totalPages - 4, 1);
            }

            for (let i = startPage; i <= endPage; i++) {
                pages.push(i);
            }
        }

        return pages;
    };

    if (isLoading) {
        return (
            <div className={cx('vendors-table')}>
                <div className={cx('loading')}>
                    Loading vendors...
                </div>
            </div>
        );
    }

    return (
        <div className={cx('vendors-table')}>
            <div className={cx('table-header')}>
                <div className={cx('header-left')}>
                    <h3>Manage Vendors</h3>
                    {searchValue && (
                        <div className={cx('search-results-text')}>
                            {searchResultsCount === 1 ? '1 vendor' : `${searchResultsCount} vendors`}
                            {searchValue && ` matching "${searchValue}"`}
                        </div>
                    )}
                </div>

                <div className={cx('header-right')}>
                    {/* Inline Search */}
                    <div className={cx('search-input-wrapper')}>
                        <div className={cx('search-input')}>
                            <Search className={cx('search-icon')} size={16} />
                            <input
                                type="text"
                                placeholder="Search vendors..."
                                value={searchValue}
                                onChange={(e) => onSearchChange(e.target.value)}
                                className={cx('search-field')}
                            />
                            {searchValue && (
                                <button
                                    type="button"
                                    className={cx('clear-search')}
                                    onClick={() => onSearchChange('')}
                                    title="Clear search"
                                >
                                    <X size={16} />
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Action Buttons */}
                    {canManageVendors && (
                        <div className={cx('action-buttons-container')}>
                            {/* Temporarily hidden import vendors button
                            <BasicButton
                                title="Import Vendors"
                                variant="neutral"
                                size="small"
                                icon="upload"
                                onClick={onImport}
                            />
                            */}
                            <BasicButton
                                title="Add Vendor"
                                variant="primary"
                                size="small"
                                onClick={onCreate}
                            />
                        </div>
                    )}
                </div>
            </div>

            {!hasVendors ? (
                <div className={cx('table-container')}>
                    <div className={cx('empty-state')}>
                        {searchValue ? `No vendors found matching "${searchValue}"` : 'No vendors available. Create your first vendor to get started.'}
                    </div>
                </div>
            ) : (
                <>
                    <div className={cx('table-container')}>
                        <table className={cx('table')}>
                            <thead>
                                <tr>
                                    <th>Vendor Name</th>
                                    <th>Company</th>
                                    <th>Contact Info</th>
                                    <th
                                        className={cx('sortable-header')}
                                        onClick={() => handleSort('status')}
                                        title="Click to sort by status"
                                    >
                                        <div className={cx('header-content')}>
                                            Status
                                            {getSortIcon('status')}
                                        </div>
                                    </th>
                                    <th
                                        className={cx('sortable-header')}
                                        onClick={() => handleSort('contractStatus')}
                                        title="Click to sort by contract status"
                                    >
                                        <div className={cx('header-content')}>
                                            Contract
                                            {getSortIcon('contractStatus')}
                                        </div>
                                    </th>
                                    <th
                                        className={cx('sortable-header')}
                                        onClick={() => handleSort('visibility')}
                                        title="Click to sort by visibility"
                                    >
                                        <div className={cx('header-content')}>
                                            Visibility
                                            {getSortIcon('visibility')}
                                        </div>
                                    </th>
                                    <th>Usage</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {paginatedVendors.map((vendor) => (
                                    <tr key={vendor.id} className={cx('vendor-row')}>
                                        <td className={cx('vendor-name')}>
                                            <div className={cx('name-cell')}>
                                                <div className={cx('vendor-title')}>{vendor.name}</div>
                                                <div className={cx('vendor-subtitle')}>
                                                    {vendor.contactPerson && (
                                                        <span className={cx('contact-person')}>
                                                            Contact: {vendor.contactPerson}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </td>
                                        <td className={cx('company')}>
                                            <div className={cx('company-info')}>
                                                <div className={cx('company-name')}>{vendor.company}</div>
                                                {vendor.website && (
                                                    <a
                                                        href={vendor.website}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className={cx('website-link')}
                                                    >
                                                        Visit Website
                                                    </a>
                                                )}
                                            </div>
                                        </td>
                                        <td className={cx('contact')}>
                                            <div className={cx('contact-info')}>
                                                <div className={cx('email')}>{vendor.email}</div>
                                                <div className={cx('phone')}>{vendor.phone}</div>
                                                {vendor.address && (
                                                    <div className={cx('address')}>{vendor.address}</div>
                                                )}
                                            </div>
                                        </td>
                                        <td className={cx('status')}>
                                            <span
                                                className={cx('status-badge', {
                                                    active: vendor.status === 'active',
                                                    inactive: vendor.status === 'inactive'
                                                })}
                                            >
                                                {vendor.status === 'active' ? 'Active' : 'Inactive'}
                                            </span>
                                        </td>
                                        <td className={cx('contract')}>
                                            <span
                                                className={cx('contract-badge', {
                                                    active: vendor.contractStatus === 'active',
                                                    inactive: vendor.contractStatus === 'inactive'
                                                })}
                                            >
                                                {vendor.contractStatus === 'active' ? 'Active' : 'Inactive'}
                                            </span>
                                        </td>
                                        <td className={cx('visibility')}>
                                            <span
                                                className={cx('visibility-badge', {
                                                    visible: vendor.isVisible,
                                                    hidden: !vendor.isVisible
                                                })}
                                            >
                                                {vendor.isVisible ? 'Visible' : 'Hidden'}
                                            </span>
                                        </td>
                                        <td className={cx('usage')}>
                                            <VendorUsageIndicator
                                                usageLocations={vendor.usage_locations || []}
                                                vendorName={vendor.name}
                                            />
                                        </td>
                                        <td className={cx('actions')}>
                                            <div className={cx('action-buttons')}>
                                                {canManageVendors ? (
                                                    <>
                                                        <button
                                                            type="button"
                                                            className={cx('fancy-action-button', 'edit-button')}
                                                            onClick={() => onEdit(vendor)}
                                                            title="Edit vendor"
                                                        >
                                                            <div className={cx('button-content')}>
                                                                <Edit3 size={16} strokeWidth={2.5} />
                                                            </div>
                                                            <div className={cx('button-ripple')} />
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className={cx('fancy-action-button', 'delete-button')}
                                                            onClick={() => onDelete(vendor)}
                                                            title="Delete vendor"
                                                        >
                                                            <div className={cx('button-content')}>
                                                                <Trash2 size={16} strokeWidth={2.5} />
                                                            </div>
                                                            <div className={cx('button-ripple')} />
                                                        </button>
                                                    </>
                                                ) : (
                                                    <span className={cx('no-permissions')}>No permissions</span>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <div className={cx('pagination-container')}>
                            <div className={cx('pagination-info')}>
                                <span className={cx('pagination-text')}>
                                    Showing {startIndex + 1}-{Math.min(endIndex, sortedVendors.length)} of {sortedVendors.length} vendors
                                    {sortConfig.field && (
                                        <span className={cx('sort-info')}>
                                            (sorted by{' '}
                                            {(() => {
                                                if (sortConfig.field === 'visibility') return 'visibility';
                                                if (sortConfig.field === 'contractStatus') return 'contract';
                                                return 'status';
                                            })()}{' '}
                                            {sortConfig.direction === 'asc' ? '↑' : '↓'})
                                        </span>
                                    )}
                                </span>
                            </div>

                            <div className={cx('pagination-controls')}>
                                <button
                                    type="button"
                                    className={cx('pagination-button', 'prev', { disabled: !canGoPrevious })}
                                    onClick={handlePreviousPage}
                                    disabled={!canGoPrevious}
                                    title="Previous page"
                                >
                                    <ChevronLeft size={16} />
                                </button>

                                <div className={cx('page-numbers')}>
                                    {getPageNumbers().map((pageNum) => (
                                        <button
                                            key={pageNum}
                                            type="button"
                                            className={cx('pagination-button', 'page-number', {
                                                active: pageNum === currentPage
                                            })}
                                            onClick={() => handlePageClick(pageNum)}
                                        >
                                            {pageNum}
                                        </button>
                                    ))}
                                </div>

                                <button
                                    type="button"
                                    className={cx('pagination-button', 'next', { disabled: !canGoNext })}
                                    onClick={handleNextPage}
                                    disabled={!canGoNext}
                                    title="Next page"
                                >
                                    <ChevronRight size={16} />
                                </button>
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

export default VendorsTable; 